"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { Calendar, Loader } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
const EVENT_TYPES = [{
  value: "protect_family_income",
  label: "Protect Family Income"
}, {
  value: "cover_final_expenses",
  label: "Cover Final Expenses"
}, {
  value: "pay_off_debts",
  label: "Pay Off Debts"
}, {
  value: "leave_inheritance",
  label: "Leave an Inheritance"
}, {
  value: "business_protection",
  label: "Business Protection"
}, {
  value: "other",
  label: "Other"
}];
export default function YourPersonalizedCoveragePlan() {
  return <section data-section-id="261360" id="your-personalized-coverage-plan" className="bg-background text-foreground py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-start">
          
          <div className="flex flex-col justify-center space-y-8">
            <div className="space-y-4">
              <h2 className="font-default text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl">
                Secure Your Family's <span className="italic font-serif text-primary">Legacy</span>
              </h2>
              <p className="text-muted-foreground text-xl max-w-[600px] font-default">
                Take the first step toward lasting peace of mind. Complete our secure questionnaire to receive a personalized coverage plan tailored to your life's unique journey.
              </p>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-1 rounded-xl bg-primary/10 blur-2xl opacity-30"></div>
            
            <Card className="relative border-t-4 border-t-primary bg-card shadow-xl rounded-lg">
              <CardHeader>
                <CardTitle className="font-default text-3xl font-bold">Request a Secure Quote</CardTitle>
                <CardDescription className="font-default text-base">
                  Your information is encrypted and handled with the utmost confidentiality.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formKey="coverage_plan_home" formId="coverage_plan_home" sectionName="Coverage Plan Form" defaultValues={{
                name: "",
                phone: "",
                email: "",
                eventType: "",
                eventDate: undefined,
                serviceArea: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                phone: {
                  required: true,
                  minLength: 10
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                eventType: {
                  required: true
                },
                eventDate: {
                  required: true
                },
                serviceArea: {
                  required: true
                }
              }} submitText="Request Secure Quote" successMessage="Thank you. A licensed agent will review your details and contact you shortly.">
                  <InquiryFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}
function InquiryFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [month, setMonth] = React.useState<Date>(new Date());
  const years = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({
      length: 100
    }, (_, i) => currentYear - i);
  }, []);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 py-8 text-center animate-in fade-in zoom-in duration-700">
        <div className="flex size-16 items-center justify-center rounded-lg bg-primary/20 text-primary">
          <svg className="size-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-default text-2xl font-semibold text-foreground">Application Received</h3>
        <p className="text-muted-foreground font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-md border-primary text-primary hover:bg-primary/5">
          Start New Application
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter your full name" className="bg-muted/30 border-border focus:ring-primary rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 000-0000" className="bg-muted/30 border-border focus:ring-primary rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-medium">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="your@email.com" className="bg-muted/30 border-border focus:ring-primary rounded-md" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField control={control} name="eventType" rules={{
        required: "Please select coverage type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-medium">Coverage Interest</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-muted/30 border-border focus:ring-primary rounded-md">
                    <SelectValue placeholder="Select coverage" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-card border-border">
                  {EVENT_TYPES.map((type, i) => <SelectItem key={type.value} value={type.value} data-index={i} className="focus:bg-primary/10">
                      {type.label}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="eventDate" rules={{
        required: "Date of birth is required"
      }} render={({
        field
      }) => <FormItem className="flex flex-col justify-end">
              <FormLabel className="font-default font-medium">Date of Birth</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button variant={"outline"} className={cn("w-full pl-3 text-left font-normal bg-muted/30 border-border rounded-md", !field.value && "text-muted-foreground")}>
                      {field.value ? (field.value as Date).toLocaleDateString() : <span>Select date</span>}
                      <SafeHydrate><Calendar className="ml-auto size-4 opacity-50" /></SafeHydrate>
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                  <div className="p-3 border-b border-border">
                    <Select onValueChange={year => {
                const newDate = new Date(month);
                newDate.setFullYear(parseInt(year));
                setMonth(newDate);
              }} value={month.getFullYear().toString()}>
                      <SelectTrigger className="w-full h-8 text-xs rounded-md">
                        <SelectValue placeholder="Select year" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {years.map((year, i) => <SelectItem key={year} value={year.toString()} data-index={i}>
                            {year}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <SafeHydrate><CalendarComponent mode="single" selected={field.value as Date} onSelect={date => {
                field.onChange(date);
                if (date) setMonth(date);
              }} month={month} onMonthChange={setMonth} disabled={date => date > new Date()} initialFocus /></SafeHydrate>
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="serviceArea" rules={{
      required: "State of residence is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-medium">State of Residence</FormLabel>
            <FormControl>
              <Input placeholder="e.g. California" className="bg-muted/30 border-border focus:ring-primary rounded-md" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-medium">Primary Reason for Coverage</FormLabel>
            <FormControl>
              <Textarea placeholder="Protect family income, cover final expenses, pay off debts, etc..." className="resize-none min-h-[100px] bg-muted/30 border-border focus:ring-primary rounded-md" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive font-default">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-default font-semibold text-lg h-14 mt-4 rounded-md shadow-lg transition-all">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Processing Securely...
          </> : "Request Secure Quote"}
      </Button>
    </Form>;
}